import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Container,
  Stack,
  CircularProgress,
  Alert,
  Fab,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import {
  AddRounded,
  RefreshOutlined,
  SupportAgentOutlined,
  TuneRounded,
} from "@mui/icons-material";
import { CheckCircleOutlineRounded } from "@mui/icons-material";
import { keyframes } from "@mui/system";

import CustomAppBar from "../../../components/CustomAppBar";
import SupportCard from "../components/SupportCard";
import SupportSummary from "../components/SupportSummary";
import AddSupportDialog from "../components/AddSupportDialog";
import { customerService } from "../services/customerService";
import {
  bgGradient,
  textPrimary,
  textSecondary,
  STORAGE_KEYS,
  POLLING_INTERVAL_MS,
  MESSAGES,
  accentColor,
} from "../../../constants/consts";
import { playIfBackground } from "../../../utils/playNotificationSound";

// --- Animasyonlar ---
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const pulseSoft = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
`;

export default function CustomerSupport({ user, onLogout }) {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contract, setContract] = useState(null);
  const pollRef = useRef(null);

  const userId = user?.user_id;
  const userType =
    user?.user_type ||
    localStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    sessionStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    "user";
  const sunucuKodu =
    user?.sunucu_kodu ||
    localStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    sessionStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    "";

  const loadSupports = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        const data = await customerService.getSupportsByUser({
          userType,
          userId,
          sunucuKodu,
        });
        setList(Array.isArray(data) ? data : []);

        // try {
        //   await customerService.changeUpdateValue({
        //     target: "user",
        //     newValue: false,
        //     userId,
        //     root: "support",
        //   });
        // } catch (e) {
        //   console.warn(
        //     "changeUpdateValue (user/support) başarısız:",
        //     e?.message,
        //   );
        // }
        // İki flag'i de sıfırla — aksi halde polling her 10sn'de
        // tekrar true döner, ses + yenileme sonsuz döngüye girer
        await Promise.all([
          customerService
            .changeUpdateValue({
              target: "user",
              newValue: false,
              userId,
              root: "support",
            })
            .catch(() => {}),
          customerService
            .changeUpdateValue({
              target: "user",
              newValue: false,
              userId,
              root: "msg",
            })
            .catch(() => {}),
        ]);
        await Promise.all([
          customerService
            .changeUpdateValue({
              target: "user",
              newValue: false,
              userId,
              root: "support",
            })
            .catch(() => {}),
          customerService
            .changeUpdateValue({
              target: "user",
              newValue: false,
              userId,
              root: "msg",
            })
            .catch(() => {}),
        ]);
      } catch (err) {
        console.error(err);
        if (!silent) setError(MESSAGES.SUPPORT_LOAD_ERROR);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userType, userId, sunucuKodu],
  );

  useEffect(() => {
    loadSupports();
    const fetchContract = async () => {
      if (!sunucuKodu) return;
      try {
        const data = await customerService.getCompanyBySunucu(sunucuKodu);
        if (Array.isArray(data) && data.length > 0) {
          setContract(data[0].contract);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchContract();
  }, [loadSupports, sunucuKodu]);

  // useEffect(() => {
  //   const tick = async () => {
  //     try {
  //       const changed = await customerService.checkDataIsUpdated({
  //         target: "user",
  //         userId,
  //         root: "support",
  //       });
  //       console.log("Support data is updated:", changed);
  //       if (changed) {
  //         playIfBackground("support");
  //         await loadSupports(true);
  //       }
  //     } catch (err) {
  //       console.warn("Polling error:", err?.message);
  //     }
  //   };
  //   pollRef.current = setInterval(tick, POLLING_INTERVAL_MS);
  //   return () => pollRef.current && clearInterval(pollRef.current);
  // }, [userId, loadSupports]);
  useEffect(() => {
    if (!userId) return;

    const tick = async () => {
      try {
        // İki kanalı paralel kontrol et:
        //   - support : destekçi cevap verdi / status değişti
        //   - msg     : destekçi yeni mesaj gönderdi
        const [supportChanged, msgChanged] = await Promise.all([
          customerService
            .checkDataIsUpdated({
              target: "user",
              userId,
              root: "support",
            })
            .catch(() => false),
          customerService
            .checkDataIsUpdated({
              target: "user",
              userId,
              root: "msg",
            })
            .catch(() => false),
        ]);

        if (!supportChanged && !msgChanged) return;

        // Destek > mesaj önceliği (aynı tick'te ikisi birden olursa)
        if (supportChanged) playIfBackground("support");
        else if (msgChanged) playIfBackground("message");

        await loadSupports(true);
      } catch (err) {
        console.warn("Polling error:", err?.message);
      }
    };
    pollRef.current = setInterval(tick, POLLING_INTERVAL_MS);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [userId, loadSupports]);
  const { activeList, completedList } = useMemo(() => {
    const isDone = (s) => (s.status || "").toLowerCase().includes("tamam");
    return {
      activeList: list.filter((s) => !isDone(s)),
      completedList: list.filter((s) => isDone(s)),
    };
  }, [list]);

  // const handleCreate = async (title, description) => {
  //   await customerService.createSupportTicket({
  //     userId,
  //     title,
  //     description,
  //     sunucuKodu,
  //     username: user?.username,
  //   });

  //   try {
  //     await customerService.changeUpdateValue({
  //       target: "nortek",
  //       newValue: true,
  //       userId: "all",
  //     });
  //   } catch (e) {
  //     console.warn("changeUpdateValue (nortek) başarısız:", e?.message);
  //   }

  //   await loadSupports(true);
  // };
  const handleCreate = async (title, description) => {
    await customerService.createSupportTicket({
      userId,
      title,
      description,
      sunucuKodu,
      username: user?.username,
    });

    // ✅ Destekçilere haber ver
    try {
      await customerService.changeUpdateValue({
        target: "nortek",
        newValue: true,
        userId: "all",
        root: "support", // ← BU SATIR VAR MI?
      });
    } catch (e) {
      console.warn("changeUpdateValue (nortek) başarısız:", e?.message);
    }

    await loadSupports(true);
  };
  const requireContractApproval = contract === "no";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        height: "100dvh",
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        "&::before": {
          content: '""',
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
          animation: `${fadeIn} 1.2s ease-out both`,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
          animation: `${fadeIn} 1.4s ease-out 0.1s both`,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <CustomAppBar
          title={MESSAGES.SUPPORT_TITLE}
          subtitle={MESSAGES.SUPPORT_SUBTITLE}
          isNotHome
          onLogout={onLogout}
          hideNotifications
          extraActions={
            <IconButton
              onClick={() => loadSupports()}
              title="Yenile"
              disabled={loading}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
                mr: 0.5,
                transition: "transform 0.2s ease, background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.22)",
                  transform: "scale(1.08)",
                },
                "&:active": { transform: "scale(0.92)" },
                "& .MuiSvgIcon-root": {
                  transition: "transform 0.6s ease",
                },
                "&:hover .MuiSvgIcon-root": {
                  transform: loading ? "none" : "rotate(180deg)",
                },
              }}
            >
              <RefreshOutlined />
            </IconButton>
          }
        />

        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: "88px", sm: "104px" },
            pb: 3,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflowY: "auto", // ← EKLE
            overflowX: "hidden", // ← EKLE
            "&::-webkit-scrollbar": { width: 6 }, // opsiyonel
            "&::-webkit-scrollbar-thumb": {
              // opsiyonel
              backgroundColor: "rgba(255,255,255,0.25)",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { background: "transparent" },
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                animation: `${fadeInUp} 0.35s ease-out both`,
              }}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {/* Özet — yumuşak giriş */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
                }}
              >
                <SupportSummary
                  total={list.length}
                  active={activeList.length}
                  completed={completedList.length}
                />
              </Box>

              {/* Tabs + liste container */}
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.98)",
                  borderRadius: 3,
                  boxShadow: "0 8px 24px rgba(15,50,65,0.12)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  overflow: "hidden",
                  animation: `${fadeInUp} 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.2s backwards`,
                }}
              >
                {/* Segmented control */}
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "rgba(15,50,65,0.03)",
                    borderBottom: "1px solid rgba(15,50,65,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      backgroundColor: "rgba(15,50,65,0.06)",
                      borderRadius: 2,
                      p: 0.5,
                      gap: 0.5,
                    }}
                  >
                    {[
                      {
                        key: "active",
                        label: MESSAGES.SUPPORT_TAB_ACTIVE,
                        count: activeList.length,
                        icon: TuneRounded,
                      },
                      {
                        key: "done",
                        label: MESSAGES.SUPPORT_TAB_DONE,
                        count: completedList.length,
                        icon: CheckCircleOutlineRounded,
                      },
                    ].map((item, idx) => {
                      const selected = tab === idx;
                      const Icon = item.icon;
                      return (
                        <Box
                          key={item.key}
                          onClick={() => setTab(idx)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 2,
                            py: 0.75,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            backgroundColor: selected ? "#fff" : "transparent",
                            boxShadow: selected
                              ? "0 2px 8px rgba(15,50,65,0.12)"
                              : "none",
                            color: selected ? textPrimary : textSecondary,
                            fontWeight: 700,
                            fontSize: 13.5,
                            transition:
                              "all 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
                            "&:hover": !selected
                              ? {
                                  color: textPrimary,
                                  backgroundColor: "rgba(0,0,0,0.03)",
                                  transform: "translateY(-1px)",
                                }
                              : { transform: "translateY(-1px)" },
                            "&:active": {
                              transform: "translateY(0) scale(0.97)",
                            },
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: 16,
                              transition: "transform 0.25s ease",
                            }}
                          />
                          <span>{item.label}</span>
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              minWidth: 22,
                              height: 20,
                              px: 0.75,
                              borderRadius: 10,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 800,
                              backgroundColor: selected
                                ? `${accentColor}1A`
                                : "rgba(0,0,0,0.06)",
                              color: selected ? accentColor : textSecondary,
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            {item.count}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    onClick={() => setDialogOpen(true)}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 700,
                      backgroundColor: "#fff",
                      color: "#045781",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                      transition:
                        "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f4f8fa",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                      },
                      "&:active": { transform: "translateY(0) scale(0.98)" },
                      "& .MuiSvgIcon-root": {
                        transition: "transform 0.25s ease",
                      },
                      "&:hover .MuiSvgIcon-root": {
                        transform: "rotate(90deg)",
                      },
                    }}
                  >
                    {MESSAGES.SUPPORT_NEW_BUTTON}
                  </Button>
                </Box>

                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    // Tab değişince içerik hafifçe belirsin
                    "& > *": {
                      animation: `${fadeIn} 0.3s ease-out both`,
                    },
                  }}
                >
                  <SupportList items={tab === 0 ? activeList : completedList} />
                </Box>
              </Box>
            </Stack>
          )}
        </Container>

        {/* Floating Action Button */}
        <Fab
          variant="extended"
          onClick={() => setDialogOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            display: { xs: "inline-flex", sm: "none" },
            background: "linear-gradient(135deg, #045781 0%, #A5AF67 100%)",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            transition:
              "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease",
            animation: `${fadeScaleIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.5s backwards`,
            "&:hover": {
              background: "linear-gradient(135deg, #033d5c 0%, #8d9757 100%)",
              transform: "scale(1.06)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
            },
            "&:active": { transform: "scale(0.96)" },
          }}
        >
          <AddRounded sx={{ mr: 1 }} />
          {MESSAGES.SUPPORT_NEW_BUTTON}
        </Fab>

        <AddSupportDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleCreate}
          requireContractApproval={requireContractApproval}
        />
      </Box>
    </Box>
  );
}

function SupportList({ items }) {
  if (!items || items.length === 0) {
    return <EmptyState />;
  }
  return (
    <Stack spacing={2}>
      {items.map((s, i) => (
        <Box
          key={s.id}
          sx={{
            // Her kart 0.04s arayla sahneye girsin (max 0.5s'de dursun)
            animation: `${fadeInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(
              i * 0.04,
              0.5,
            )}s backwards`,
          }}
        >
          <SupportCard support={s} />
        </Box>
      ))}
    </Stack>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        py: 6,
        px: 3,
        textAlign: "center",
        animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.15s backwards`,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: "auto",
          mb: 2,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${accentColor}14`,
          animation: `${pulseSoft} 2.6s ease-in-out 1s infinite`,
        }}
      >
        <SupportAgentOutlined sx={{ fontSize: 30, color: accentColor }} />
      </Box>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color={textPrimary}
        gutterBottom
      >
        {MESSAGES.SUPPORT_EMPTY_TITLE}
      </Typography>
      <Typography
        variant="body2"
        color={textSecondary}
        sx={{ maxWidth: 340, mx: "auto" }}
      >
        {MESSAGES.SUPPORT_EMPTY_MESSAGE}
      </Typography>
    </Box>
  );
}
